/*
 * Thanks
 * https://codepen.io/linuscodes/pen/gJVEXe 🙏
 */

// Convert rgba array to hex
const rgbToHex = rgb => {
  const hex = [];

  for (let i = 0; i < rgb.length - 1; i += 1) {
    hex[i] = rgb[i].toString(16);

    if (hex[i].length < 2) {
      hex[i] = `0${hex[i]}`; // Pad with leading zero
    }
  }

  return `#${hex.join('')}`;
};

function ColorGenerator({ rotation = 90, lightBackground = false } = {}) {
  /* Create the color palette on canvas */

  // Color variables
  let baseColor;
  let secondColor;
  let thirdColor;

  // Canvas variables
  let canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas size
  const canvasWidth = 80;
  const canvasHeight = 80;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const randomNum = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Here come the colors
  const Color = function Color(hue, sat, light) {
    // Settings
    // Play with these to change the types of colors generated
    this.minHue = 0;
    this.maxHue = 360;

    this.minSat = 75;
    this.maxSat = 100;

    this.minLight = 65;
    this.maxLight = 80;

    this.scaleLight = 15;

    // Darker colors for a light background
    if (lightBackground) {
      this.minLight = 5;
      this.maxLight = 35;
    }

    // Set hue
    this.hue = hue || randomNum(this.minHue, this.maxHue);

    // Redo if ugly hue is generated
    // Because magenta is hideous
    if (this.hue > 288 && this.hue < 316) {
      this.hue = randomNum(316, 360);
    } else if (this.hue > 280 && this.hue < 288) {
      this.hue = randomNum(260, 280);
    }

    this.sat = sat || randomNum(this.minSat, this.maxSat);
    this.light = light || randomNum(this.minLight, this.maxLight);

    this.hsl = `hsl(${this.hue}, ${this.sat}%, ${this.light}%)`;
  };

  // Change hue by rotation number
  Color.prototype.changeHue = function changeHue(hue, rotate) {
    return hue + rotate > this.maxHue
      ? hue + rotate - this.maxHue
      : hue + rotate;
  };

  // Scale lightness while keeping within limits
  Color.prototype.changeLight = function changeLight(light) {
    return light + this.scaleLight > this.maxLight
      ? this.maxLight
      : light + this.scaleLight;
  };

  // Now generate the three main colors of the palette
  const setColors = () => {
    baseColor = new Color();

    secondColor = new Color(
      baseColor.changeHue(baseColor.hue, rotation),
      baseColor.sat,
      baseColor.changeLight(baseColor.light),
    );

    thirdColor = new Color(
      baseColor.changeHue(baseColor.hue, rotation + rotation),
      baseColor.sat,
      baseColor.changeLight(baseColor.light),
    );
  };

  // Draw colors on canvas
  const drawColor = (x, y, width, height, color, blendMode, alpha) => {
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha || 1;
    ctx.globalCompositeOperation = blendMode || 'source-over';
    ctx.rect(x, y, width, height);
    ctx.fill();
    ctx.closePath();
    ctx.restore();
  };

  // Draw the canvas
  const drawCanvas = () => {
    // Top color
    drawColor(0, 0, canvasWidth, canvasHeight, baseColor.hsl);

    // Middle color
    drawColor(0, canvasHeight / 3, canvasWidth, canvasHeight, secondColor.hsl);

    // Bottom color
    drawColor(
      0,
      canvasHeight / 3 + canvasHeight / 3,
      canvasWidth,
      canvasHeight,
      thirdColor.hsl,
    );

    // Left color overlay (lightens with yellow)
    drawColor(0, 0, canvasWidth / 3, canvasHeight, '#fcd108', 'screen', 0.75);

    // Right color overlay (darkens with blue)
    drawColor(
      canvasWidth / 3 + canvasWidth / 3,
      0,
      canvasWidth / 3,
      canvasHeight,
      '#094078',
      'overlay',
    );

    // Full color overlays (creates a cast over all colors for a more uniform, moderated look)
    drawColor(0, 0, canvasWidth, canvasHeight, '#ffcc00', 'overlay', 0.25);
    drawColor(0, 0, canvasWidth, canvasHeight, '#f13a6d', 'lighten', 0.25);
    drawColor(0, 0, canvasWidth, canvasHeight, '#092559', 'screen', 0.5);
  };

  /* Import the color palette to DOM */

  // Pick a color from canvas
  const getColor = (x, y) => {
    const rgba = ctx.getImageData(x, y, 1, 1).data;
    rgba[3] = 1; // set alpha channel from 255 to 1

    return rgba;
  };

  // Now get all nine colors from canvas
  const createPalette = () => {
    const allColors = [
      getColor(10, 10),
      getColor(canvasWidth / 2 + 10, 0 + 10),
      getColor(canvasWidth - 10, 0),

      getColor(10, canvasHeight / 2 + 10),
      getColor(canvasWidth / 2 + 10, canvasHeight / 2 + 10),
      getColor(canvasWidth - 10, canvasHeight / 2),

      getColor(10, canvasHeight - 10),
      getColor(canvasWidth / 2 + 10, canvasHeight - 10),
      getColor(canvasWidth - 10, canvasHeight - 10),
    ];

    const finalColors = allColors.reduce((p, c) => {
      return [...p, { raw: c, rgba: `rgba(${c.join(',')})`, hex: rgbToHex(c) }];
    }, []);

    return finalColors;
  };

  // Run all the palette functions
  return () => {
    setColors();
    drawCanvas();
    const palette = createPalette();
    canvas = null;

    return palette;
  };
}

export default function generateColors({
  rotation = 90,
  lightBackground = false,
} = {}) {
  return ColorGenerator({ rotation, lightBackground })();
}
